<template>
  <div class="p-grid">
    <div class="p-col-12">
      <div class="card">
        <BillingFeatureGate feature="technician_earnings"
                            variant="page"
                            :title="$t('billing.features.technician_earnings.pageTitle')"
                            :description="$t('billing.features.technician_earnings.tooltip')">
          <Toolbar class="p-mb-4">
            <template #start>
              <div style="font-size: 1.4em; color: inherit" class="p-mr-3">{{ $t('Earnings') }}</div>
            </template>
            <template #end>
              <DatesRangeCalendar :disabled="dataTableIsLoading" @change-dates-range="changeDatesRange" v-show="true"/>
            </template>
          </Toolbar>
          <div class="p-d-flex p-ai-center p-flex-wrap p-mb-4 p-mt-0">
            <div class="result-block p-m-1">
              {{ $t('Total sum') }}: <b>{{ formatCurrency(totalPaymentsSum) }}</b>
            </div>
            <template v-if="credit || debit">
              <div class="result-block p-m-1">
                {{ $t('Credit') }}: <span class="green-color">{{ formatCurrency(credit) }}</span>
              </div>
              <div class="result-block p-m-1">
                {{ $t('Debit') }}: <span class="warning-color">{{ formatCurrency(debit) }}</span>
              </div>
            </template>
          </div>
          <Spinner v-if="isLoading"/>
          <DesktopDataTable v-else
                            :dataTableIsLoading="dataTableIsLoading"
                            :itemsArrayIsEmpty="itemsArrayIsEmpty"
                            :sort="sort"
                            :showSearchInput="false"
                            :showButtons="actionButtonsCount > 0"
                            :actionButtonsCount="actionButtonsCount"
                            @sort-items="sortItems"
                            :headers="[
                            {name: 'order', title: $t('Order'), sortable: false},
                            {name: 'car', title: $t('Car'), sortable: false},
                            {name: 'name', title: $t('Name'), sortable: false},
                            // {name: 'qty', title: $t('Qty'), hint: $t('employeePaymentQtyTooltip'), sortable: false},
                            // {name: 'share_percent', title: $t('shareLabel'), sortable: false},
                            // {name: 'technician_amount', title: $t('employeePaymentBaseLabel'), sortable: false},
                            // {name: 'time', title: $t('technicianTimeLabel'), sortable: false},
                            {
                              name: 'service_calculation',
                              title: $t('employeePaymentServiceCalculationLabel'),
                              sortable: false
                            },
                            {
                              name: 'share_percent',
                              title: $t('shareLabel'),
                              minWidth: '60px',
                              sortable: false
                            },
                            {
                              name: 'technician_calculation',
                              title: $t('employeePaymentTechnicianCalculationLabel'),
                              sortable: false
                            },
                            {name: 'percent', title: $t('Percent'), sortable: false},
                            {name: 'sum', title: $t('employeePaymentAmountLabel'), sortable: false},
                            {name: 'date', title: $t('Date'), sortable: false},
                          ]">
            <template v-slot:body>
              <tr v-for="(item, index) of items" :key="index"
                  :class="{'updated-item': isItemUpdated(item.id)}">
                <td class="table-data">
                <span v-if="item.order?.number">
                  {{ item.order.number }}
                </span>
                  <i v-else class="ti-minus"/>
                </td>

                <td class="table-data">
                <span v-if="item.car && formatCarLabel(item.car)">
                  {{ formatCarLabel(item.car) }}
                </span>
                  <i v-else class="ti-minus"/>
                </td>

                <td class="table-data">
                  <span v-if="item.description">{{ item.description }}</span>
                  <i v-else class="ti-minus"/>
                </td>

                <td class="table-data">
                  <div>
                  <span v-if="item.service_total_amount !== null && item.service_total_amount !== undefined">
                    {{ formatCurrency(item.service_total_amount) }}
                  </span>

                    <i v-else class="ti-minus"/>
                  </div>

                  <div v-if="hasTime(item.total_time)"
                       class="payment-calculation-cell__secondary">
                    <i class="ti-time"/>
                    {{ formatHoursAndMinutes(item.total_time) }}
                  </div>
                </td>

                <!--              <td class="table-data number-cell">-->
                <!--                <span v-if="item.qty !== null && item.qty !== undefined">-->
                <!--                  {{ formatDecimal(item.qty) }}-->
                <!--                </span>-->
                <!--                <i v-else class="ti-minus"/>-->
                <!--              </td>-->

                <td class="table-data number-cell">
                <span v-if="item.share_percent !== null && item.share_percent !== undefined">
                  {{ formatDecimal(item.share_percent, false) }}%
                </span>
                  <i v-else class="ti-minus"/>
                </td>

                <!--              <td class="table-data number-cell">-->
                <!--                <span v-if="getPaymentTechnicianAmount(item) !== null">-->
                <!--                  {{ formatCurrency(getPaymentTechnicianAmount(item)) }}-->
                <!--                </span>-->
                <!--                <i v-else class="ti-minus"/>-->
                <!--              </td>-->

                <!--              <td class="table-data number-cell">-->
                <!--                <span v-if="hasTime(item.time)">-->
                <!--                  {{ formatHoursAndMinutes(item.time) }}-->
                <!--                </span>-->
                <!--                <i v-else class="ti-minus"/>-->
                <!--              </td>-->

                <td class="table-data">
                <span v-if="isFullShare(item.share_percent)"
                      class="payment-calculation-cell__same">
                  {{ $t('sameAsServiceCalculationLabel') }}
                </span>

                  <template v-else>
                    <div>
                    <span v-if="getPaymentTechnicianAmount(item) !== null">
                      {{ formatCurrency(getPaymentTechnicianAmount(item)) }}
                    </span>
                      <i v-else class="ti-minus"/>
                    </div>

                    <div v-if="hasTime(item.time)"
                         class="payment-calculation-cell__secondary">
                      <i class="ti-time"/>
                      {{ formatHoursAndMinutes(item.time) }}
                    </div>
                  </template>
                </td>

                <td class="table-data number-cell">
                <span v-if="item.percent !== null && item.percent !== undefined && item.order_position_id">
                  {{ formatDecimal(item.percent) }}%
                </span>
                  <i v-else class="ti-minus"/>
                </td>

                <td class="table-data number-cell">
                <span :class="item.sum >= 0 ? 'green-color' : 'warning-color'">
                  <span v-if="item.sum >= 0">+</span>{{ formatCurrency(item.sum) }}
                </span>
                </td>

                <td class="table-date-line">
                  <span v-if="item.date">{{ formatDate(item.date) }}</span>
                  <i v-else class="ti-minus"/>
                </td>
              </tr>
            </template>
          </DesktopDataTable>

          <OwnPaginator v-show="!itemsArrayIsEmpty"
                        :rows="pagination.perPage"
                        :currentPage="pagination.currentPage - 1"
                        :totalPages="pagination.pageCount"
                        :totalRecords="pagination.totalCount"
                        :rowsPerPageOptions="[5,10,25,50,100,200]"
                        :pageLinkSize="11"/>

          <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>
        </BillingFeatureGate>
      </div>
    </div>
  </div>
</template>
<script>
import httpClient from '@/services/http.services'
import datatableMixins from "@/mixins/datatableMixins";
import formatMixins from "@/mixins/formatMixins";
import DatesRangeCalendar from "@/components/DatesRangeCalendar";
import settings from "@/settings";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import billingFeaturesMixins from '@/mixins/billingFeaturesMixins'
import BillingFeatureGate from '@/components/billing/BillingFeatureGate.vue'

export default {
  mixins: [ showErrorsMixins, datatableMixins, formatMixins, billingFeaturesMixins ],
  components: { DatesRangeCalendar, BillingFeatureGate },
  emits: ['update-items', 'updateItems'],
  props: {
    user: Object,
  },
  data() {
    return {
      totalPaymentsSum: 0,
      debit: 0,
      credit: 0,
      fromTimestamp: null,
      toTimestamp: null,
      fieldsExpand: 'car,order',
      apiLink: 'employee-payment/get-master-payments',
      sort: {
        column: 'date',
        direction: '-',
      },
      routePath: '/employee-earnings',
    }
  },
  watch: {
    billingEntitlementsReady() {
      this.initializeFeaturePage()
    },
  },
  mounted() {
    this.scrollToTop()
    this.initializeFeaturePage()
  },
  methods: {
    initializeFeaturePage() {
      if (!this.billingEntitlementsReady || !this.canUseTechnicianEarnings) return
      if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) return false
      this.checkRouteQuery()
      this.generateThisMonthRange()
      this.getPageData()
    },
    getPageData() {
      if (!this.canUseTechnicianEarnings) return
      this.getItems()
    },
    isFullShare(value) {
      const share = Number(value)

      return Number.isFinite(share) && share === 100
    },
    getPaymentTechnicianAmount(item) {
      if (
          item.service_total_amount !== null &&
          item.service_total_amount !== undefined &&
          item.share_percent !== null &&
          item.share_percent !== undefined
      ) {
        return +(Number(item.service_total_amount) * (Number(item.share_percent) / 100)).toFixed(2)
      }

      if (
          item.service_sell_price !== null &&
          item.service_sell_price !== undefined &&
          item.qty !== null &&
          item.qty !== undefined
      ) {
        return +(Number(item.service_sell_price) * Number(item.qty)).toFixed(2)
      }

      return null
    },
    changeDatesRange({ fromTimestamp, toTimestamp }, getItems = false) {
      this.fromTimestamp = fromTimestamp
      this.toTimestamp = toTimestamp
      if (getItems) {
        this.getItems()
      }
    },
    async getItems(showSpinner = false) {
      if (!this.canUseTechnicianEarnings) return
      if (!this.userCanGetData || this.fetchInFlight) return
      if (showSpinner) this.isLoading = true

      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true

      const ctrl = this.setController?.('masterPayments')

      try {
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          method: 'post',
          params: {
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.searchData,
            expand: this.fieldsExpand,
          },
          data: {
            userId: this.$route.params.id,
            dateFrom: this.fromTimestamp,
            dateTo: this.toTimestamp,
          },
          signal: ctrl?.signal
        })

        if (status === 200 && data) {
          const items = Array.isArray(data.payments_list) ? data.payments_list : []
          const hasPaginationHeaders = !!headers['x-pagination-total-count']

          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = items.length
            this.pagination.pageCount  = 1
          }

          this.items = items
          this.totalPaymentsSum = data.total_sum ?? 0
          this.debit = data.debit ?? 0
          this.credit = data.credit ?? 0

          if (!items.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.items = []
          this.totalPaymentsSum =  0
          this.debit = 0
          this.credit = 0
          this.showUnknownError()
        }
      } catch (err) {
        this.items = []
        this.totalPaymentsSum = 0
        this.debit = 0
        this.credit = 0
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.dataTableIsLoading = false

        if (this._abortors?.masterPayments === ctrl) {
          this._abortors.masterPayments = null
        }
      }
    }
  },
  computed: {
    actionButtonsCount() {
      return 0
    },
  }
}
</script>
<style scoped lang="scss">
.total-sum-block {
  font-size: 1.4em;
  //font-weight: 400;
}

.table-data {
  height: 44px;
}

.result-block {
  padding-right: 16px;
}

.number-cell {
  text-align: right;
  white-space: nowrap;
}

.payment-calculation-cell__secondary {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 3px;
  color: var(--text-color-secondary);
  font-size: 0.9em;
  white-space: nowrap;
}

.payment-calculation-cell__same {
  color: var(--text-color-secondary);
  font-size: 0.9em;
  white-space: nowrap;
}
</style>
